import {
    ActionStatusHelper,
    FeedBackType,
    MessageHelper,
    UIAction,
    EXPORT_FORMATS,
    UIFeedbackHooks,
    DateFormatHelper,
    DomainType,
    EntityHook,
    RecordMap,
    Entity,
    useAppEnum,
    ExportUIData,
    AppEnum,
    AppEnumHelper,
} from "@/Shared";


import { useGenerateDomain } from "@/Shared/Logic/Hooks/useGenerateDomain";
import { useExportDomain } from "@/Shared/Logic/Hooks/useExportDomain";
import { AccessRightModel, DocumentModel } from "@/Domains";

interface ExportableVM<T extends Entity> {
    id : number;
    model: T;
    isDeleted: boolean;
    documentMap: () => RecordMap<DocumentModel> | undefined
}


interface ExportModuleProps<T extends Entity, VM extends ExportableVM<T>> {
    accessRight: AccessRightModel;
    uiFeedbackHooks: UIFeedbackHooks;
    entityVM: VM | undefined | null;
    domainType: DomainType;
    domainLabel: string;
    entityHook: EntityHook<any>;
    noFormDocTypes? : string[];
    supportedDocTypes? : string[];
    contextFn?: (documentType: string, model: T) => Record<string, any>;
}

/**
 * useExportAction hook
 *
 * Generic export orchestration hook responsible for:
 * - document generation
 * - generated document export
 * - export modal composition
 * - export-related UI actions
 *
 * The hook centralizes the complete export workflow
 * for any exportable domain entity.
 *
 * UI Philosophy:
 * The export modal intentionally separates:
 *
 * 1. Static document templates
 *    -> generation actions
 *
 * 2. Generated documents
 *    -> export actions
 *
 * This separation improves scalability and preserves
 * clarity when many generated documents exist.
 *
 * @date: May 15, 2026
 * @author: Samano CASTRE
 *
 * @param props ExportModuleProps
 */
export function useExportAction<T extends Entity, VM extends ExportableVM<T>>(props: ExportModuleProps<T, VM>) {
    const {
        accessRight,
        uiFeedbackHooks,
        entityVM,
        domainType,
        domainLabel,
        entityHook,
        noFormDocTypes,
        supportedDocTypes,
        contextFn
    } = props;


    const { executeGenerate } = useGenerateDomain({
        domainType,
        canGenerate: accessRight.canExport(domainType),
        generateAction: entityHook.generateAction,
        uiFeedbackHooks
    });
    const { executeExport } = useExportDomain({
        domainType,
        canExport: accessRight.canExport(domainType),
        exportAction: entityHook.exportAction,
        uiFeedbackHooks
    });

    const { 
        isLoading: generateIsLoading, 
        isSucceeded: generateIsSucceed 
    } = ActionStatusHelper.is(entityHook.generateAction.response.status);

    const {DocumentEnums} = useAppEnum();

    const docCategory =  AppEnumHelper.getByValue(DocumentEnums.DocumentCategoryEnum, domainType);

    /**
     * Handles PDF export for an already generated document.
     *
     * Delegates export execution to the shared export hook.
     * A valid document identifier is required.
     *
     * @param documentId Document identifier
     */
    const handleExportPDF = async (documentId?: number) => {
        if (!documentId) {

            uiFeedbackHooks.alertHook?.openAlert?.({
                type: FeedBackType.ERROR,
                message: MessageHelper.TECHNICL_ERROR(domainLabel)
            });
            return;
        }
        await executeExport({ context: { documentId } }, EXPORT_FORMATS.PDF);
    }

    /**
     * Handles PDF generation for a document template.
     *
     * Builds generation context from either:
     * - the provided custom context factory
     * - the default property-based context
     *
     * Delegates generation execution to the shared
     * generation hook.
     *
     * @param documentType Target document type
     */
    const handleGeneratePDF = async (documentType: string, form? : Record<string, any>) => {

        const model = entityVM?.model;

        if (!model) {
            console.error("Invalid Property model for generating pdf.");

            uiFeedbackHooks.alertHook?.openAlert?.({
                type: FeedBackType.ERROR,
                message: MessageHelper.TECHNICL_ERROR(domainLabel)
            });
            return;
        }

        let context = contextFn
            ? contextFn(documentType, model)
            : { documentType, id: model.id };

        context = {
            ...context,
            ...(form ?? {})
        };
        
        await executeGenerate({ context }, EXPORT_FORMATS.PDF);
    }

    /**
     * Builds export modal data structure.
     *
     * Returns two UI sections:
     *
     * 1. Static document templates
     *    -> generation actions
     *
     * 2. Generated documents
     *    -> export actions
     *
     * Generated documents are dynamically resolved
     * from the entity document map.
     */
    const getExportableData = () : ExportUIData => {

        if (!entityVM || entityVM?.isDeleted) {
            return {} as ExportUIData;
        }

        const filterSupportedType = (documentTypeEnum: AppEnum) => 
            !supportedDocTypes ||  supportedDocTypes.includes(documentTypeEnum.value as string);

        const toGenerate = () => {
            return (docCategory?.children ?? [])
                .filter(filterSupportedType)
                .map(documentTypeEnum => {
                return {
                    type : String(documentTypeEnum.value),
                    label: documentTypeEnum.label,
                    handler: (form? : Record<string, any>) => handleGeneratePDF(String(documentTypeEnum.value), form)
                }
            });
        }

        const toDownload = () => {
            return entityVM.documentMap()?.toArray()?.map(
                (document: DocumentModel) => {
                    return {
                        type : document.documentType,
                        label: document.documentTitle + " (" + DateFormatHelper.toLocaleDateString(document.createdAt) + ")",
                        handlers: { export: () => handleExportPDF(document.id) }
                    };
                }) ?? [];
        }

        return {
            toGenerate : toGenerate(),
            toDownload : toDownload(),
            id : entityVM.id,
            noFormDocTypes
        };
    }

    /**
     * Builds export-related UI actions available
     * for the current entity.
     *
     * The generated action opens the export modal
     * containing:
     * - available document generators
     * - generated document exports
     *
     * Access rights are enforced before allowing
     * modal interaction.
     *
     * @returns UIAction[]
     */
    const computeUIActions = (): UIAction[] => {
        const actions: UIAction[] = [];
        if (!entityVM  || entityVM?.isDeleted) {
            return actions;
        }

        const canExport = accessRight.canExport(domainType);

        actions.push({
            key: `${domainType}-execute-${entityVM.id}`,
            label: "Exporter",
            onClick: () => canExport
                ? uiFeedbackHooks.exportUIHook?.openExport(getExportableData())
                : undefined,

            placement: "sub",
            type: 'execute',
            icon: 'FaFilePdf',
            disabled: !canExport,
            loading: generateIsLoading,
            tooltip: canExport
                ? `Cliquez pour exporter un document.`
                : `Vous n'avez pas les droits suffisants pour exporter.`
        });
        return actions;
    };

    /**
     * Public API exposed to the component layer
     */
    return {
        generateIsSucceed,
        generateIsLoading,
        computeUIActions,
    }
}